import React, { useState, useEffect } from 'react';
import { 
  Droplets, 
  AlertTriangle, 
  Gauge, 
  Timer,
  TrendingUp,
  TrendingDown,
  Activity,
  MapPin,
  Zap
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useIntlFormatting } from '../utils/intl';
import { iotApi, DashboardStats } from '../lib/iotApi';
import { IS_AWS_MODE } from '../config/frontend';

interface KPICard {
  title: string;
  value: string | number;
  unit: string;
  change: number;
  changeLabel: string;
  icon: React.ElementType;
  color: string;
}

interface ChartData {
  time: string;
  moisture: number;
  pressure: number;
  flow: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['dashboard', 'common']);
  const { formatNumber, formatUnit } = useIntlFormatting();
  const [timeRange, setTimeRange] = useState('24h');
  const [awsStats, setAwsStats] = useState<DashboardStats | null>(null);
  const [awsError, setAwsError] = useState('');

  useEffect(() => {
    if (!IS_AWS_MODE) return;
    let active = true;
    iotApi.get('/iot/dashboard')
      .then((data) => {
        if (active) setAwsStats(data);
      })
      .catch((error) => {
        if (active) setAwsError((error as Error).message);
      });
    return () => {
      active = false;
    };
  }, []);

  if (IS_AWS_MODE) {
    const cards = [
      { label: 'Nodes', value: awsStats?.nodes_total ?? '—', icon: Activity, color: 'var(--color-primary)' },
      { label: 'Nodes en ligne', value: awsStats?.nodes_online ?? '—', icon: Zap, color: 'var(--color-success)' },
      { label: 'Vannes ouvertes', value: awsStats?.valves_open ?? '—', icon: Droplets, color: 'var(--color-info)' },
      { label: 'Alertes ouvertes', value: awsStats?.open_alerts ?? '—', icon: AlertTriangle, color: 'var(--color-warning)' },
      { label: 'Commandes', value: awsStats?.commands_total ?? '—', icon: Timer, color: 'var(--text-primary)' },
      { label: 'Commandes réussies', value: awsStats?.commands_success ?? '—', icon: TrendingUp, color: 'var(--color-success)' },
    ];
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">Tableau de bord AWS</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            Organisation {user?.org_id || 'non définie'} · données opérationnelles DynamoDB
          </p>
        </div>
        {awsError && (
          <div className="card border border-[var(--color-danger)] text-[var(--color-danger)]">
            Impossible de charger le tableau de bord : {awsError}
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((card) => (
            <div key={card.label} className="card text-center">
              <card.icon className="w-6 h-6 mx-auto mb-2" style={{ color: card.color }} />
              <div className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</div>
              <div className="text-sm text-[var(--text-secondary)] mt-1">{card.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Mock KPI data
  const kpis: KPICard[] = [
    {
      title: t('dashboard:kpis.waterSaved.title'),
      value: formatNumber(1247),
      unit: t('dashboard:kpis.waterSaved.unit'),
      change: 12.5,
      changeLabel: t('dashboard:kpis.waterSaved.comparison'),
      icon: Droplets,
      color: 'var(--color-info)',
    },
    {
      title: t('dashboard:kpis.activeAlerts.title'),
      value: 3,
      unit: t('dashboard:kpis.activeAlerts.unit'),
      change: -2,
      changeLabel: t('dashboard:kpis.activeAlerts.comparison'),
      icon: AlertTriangle,
      color: 'var(--color-warning)',
    },
    {
      title: t('dashboard:kpis.avgMoisture.title'),
      value: 68,
      unit: t('dashboard:kpis.avgMoisture.unit'),
      change: 5.2,
      changeLabel: t('dashboard:kpis.avgMoisture.comparison', { target: 65 }),
      icon: Gauge,
      color: 'var(--color-success)',
    },
    {
      title: t('dashboard:kpis.pumpRuntime.title'),
      value: formatNumber(24.7, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      unit: t('dashboard:kpis.pumpRuntime.unit'),
      change: -8.3,
      changeLabel: t('dashboard:kpis.pumpRuntime.comparison'),
      icon: Timer,
      color: 'var(--color-primary)',
    },
  ];

  // Mock chart data
  const chartData: ChartData[] = [
    { time: '00:00', moisture: 65, pressure: 2.4, flow: 3.2 },
    { time: '04:00', moisture: 62, pressure: 2.8, flow: 4.1 },
    { time: '08:00', moisture: 58, pressure: 3.2, flow: 5.8 },
    { time: '12:00', moisture: 54, pressure: 3.6, flow: 6.2 },
    { time: '16:00', moisture: 61, pressure: 3.1, flow: 4.9 },
    { time: '20:00', moisture: 67, pressure: 2.6, flow: 3.4 },
  ];

  const recentAlerts = [
    {
      id: '1',
      type: 'leak',
      zone: 'Zone A-1',
      message: 'Potential leak detected: flow rate 7.2 m³/h exceeds baseline',
      time: '2 hours ago',
      severity: 'critical'
    },
    {
      id: '2', 
      type: 'low_moisture',
      zone: 'Zone B-3',
      message: 'Low soil moisture: 45% below target minimum 60%',
      time: '4 hours ago',
      severity: 'warning'
    },
    {
      id: '3',
      type: 'ph_out_of_range',
      zone: 'Zone C-2',
      message: 'pH out of range: 8.2 (optimal: 5.5-7.8)',
      time: '6 hours ago',
      severity: 'info'
    }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'var(--color-danger)';
      case 'warning': return 'var(--color-warning)';
      case 'info': return 'var(--color-info)';
      default: return 'var(--text-secondary)';
    }
  };

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-primary rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full transform translate-x-8 -translate-y-8"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-10 rounded-full transform -translate-x-4 translate-y-4"></div>
        <div className="relative z-10">
          <h1 className="text-heading-1 mb-2">
            {t('dashboard:welcomeBack', { name: user?.name?.split(' ')[0] || 'User' })} 👋
          </h1>
          <p className="text-lg opacity-90 mb-4">
            {t('dashboard:systemStatus', { count: 12 })}
          </p>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5" />
              <span className="text-sm">{t('dashboard:allSystemsOperational')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5" />
              <span className="text-sm">{t('dashboard:regionLocation')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {kpis.map((kpi, index) => (
          <div key={index} className="card hover:shadow-lg" data-testid={`kpi-card-${index}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${kpi.color}15` }}>
                <kpi.icon className="w-6 h-6" style={{ color: kpi.color }} />
              </div>
              <div className={`flex items-center space-x-1 text-sm ${
                kpi.change >= 0 ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'
              }`}>
                {kpi.change >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                <span className="font-medium">{Math.abs(kpi.change)}%</span>
              </div>
            </div>
            <div className="mb-2">
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-[var(--text-primary)]">{kpi.value}</span>
                <span className="text-sm text-[var(--text-secondary)]">{kpi.unit}</span>
              </div>
            </div>
            <div className="text-xs text-[var(--text-secondary)]">
              {kpi.changeLabel}
            </div>
            <div className="text-sm font-medium text-[var(--text-primary)] mt-1">
              {kpi.title}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Moisture & Pressure Chart */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-heading-3 text-[var(--text-primary)]">
              {t('dashboard:charts.systemMonitoring.title')}
            </h2>
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
              className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
              data-testid="time-range-select"
            >
              <option value="24h">{t('dashboard:charts.systemMonitoring.last24h')}</option>
              <option value="7d">{t('dashboard:charts.systemMonitoring.last7d')}</option>
              <option value="30d">{t('dashboard:charts.systemMonitoring.last30d')}</option>
            </select>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="time" stroke="var(--text-secondary)" />
                <YAxis stroke="var(--text-secondary)" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--surface)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-lg)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="moisture" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  name={t('dashboard:charts.systemMonitoring.moisture')}
                />
                <Line 
                  type="monotone" 
                  dataKey="pressure" 
                  stroke="var(--color-info)" 
                  strokeWidth={2}
                  name={t('dashboard:charts.systemMonitoring.pressure')}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Flow Rate Chart */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-heading-3 text-[var(--text-primary)]">
              {t('dashboard:charts.flowRate.title')}
            </h2>
            <div className="text-sm text-[var(--text-secondary)]">{t('dashboard:charts.flowRate.unit')}</div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="time" stroke="var(--text-secondary)" />
                <YAxis stroke="var(--text-secondary)" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--surface)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-lg)'
                  }}
                />
                <Bar 
                  dataKey="flow" 
                  fill="var(--color-accent)" 
                  radius={[4, 4, 0, 0]}
                  name={t('dashboard:charts.flowRate.flowRate')}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-heading-3 text-[var(--text-primary)]">
            {t('dashboard:recentAlerts.title')}
          </h2>
          <button className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-700)] font-medium">
            {t('dashboard:recentAlerts.viewAll')}
          </button>
        </div>
        <div className="space-y-4">
          {recentAlerts.map((alert) => (
            <div key={alert.id} className="flex items-start space-x-4 p-4 rounded-lg border border-[var(--border-light)] hover:bg-[var(--gray-50)] transition-colors">
              <div 
                className="w-2 h-2 rounded-full mt-2"
                style={{ backgroundColor: getSeverityColor(alert.severity) }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {alert.zone}
                  </p>
                  <span className="text-xs text-[var(--text-tertiary)]">
                    {alert.time}
                  </span>
                </div>
                <p className="text-sm text-[var(--text-secondary)]">
                  {alert.message}
                </p>
              </div>
              <button className="text-xs px-3 py-1 rounded-full border border-[var(--border-medium)] hover:bg-[var(--gray-100)] transition-colors">
                {t('dashboard:recentAlerts.acknowledge')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center">
          <div className="w-12 h-12 bg-[var(--color-primary-100)] rounded-lg flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-6 h-6 text-[var(--color-primary)]" />
          </div>
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">{t('dashboard:quickActions.viewMap.title')}</h3>
          <p className="text-body-sm text-[var(--text-secondary)] mb-4">
            {t('dashboard:quickActions.viewMap.description')}
          </p>
          <button className="btn-secondary w-full" data-testid="view-map-button">
            {t('dashboard:quickActions.viewMap.button')}
          </button>
        </div>

        <div className="card text-center">
          <div className="w-12 h-12 bg-[var(--color-warning-100)] rounded-lg flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-[var(--color-warning)]" />
          </div>
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">{t('dashboard:quickActions.manageAlerts.title')}</h3>
          <p className="text-body-sm text-[var(--text-secondary)] mb-4">
            {t('dashboard:quickActions.manageAlerts.description')}
          </p>
          <button className="btn-secondary w-full" data-testid="manage-alerts-button">
            {t('dashboard:quickActions.manageAlerts.button')}
          </button>
        </div>

        <div className="card text-center">
          <div className="w-12 h-12 bg-[var(--color-info-100)] rounded-lg flex items-center justify-center mx-auto mb-4">
            <Zap className="w-6 h-6 text-[var(--color-info)]" />
          </div>
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">{t('dashboard:quickActions.deviceStatus.title')}</h3>
          <p className="text-body-sm text-[var(--text-secondary)] mb-4">
            {t('dashboard:quickActions.deviceStatus.description')}
          </p>
          <button className="btn-secondary w-full" data-testid="device-status-button">
            {t('dashboard:quickActions.deviceStatus.button')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
